import React from 'react';
import { LuCirclePlus, LuUpload } from 'react-icons/lu';
import PortraitScoreBar from '../oracle/PortraitScoreBar';
import GlowupDatePicker from './GlowupDatePicker';
import { formatScore10, formatScanDate } from './format';

// BEFORE / AFTER frame in the glowup composer. 3:4 frame, gold border when it is
// the active pick target, plus-circle when empty, real thumbnail + eye-line
// score bar when filled from archive, plain preview when freshUpload.
//
// The caption + upload action live in GlowupSlotFooter (a sibling row) so the
// arrow medallion can center on the FRAME band instead of the whole slot.
const GlowupSlot = ({ label, entry, freshUpload, active, onActivate }) => {
  const filled = Boolean(entry || freshUpload);
  const previewUrl = freshUpload
    ? freshUpload.previewUrl
    : entry
      ? entry.thumbnail_url
      : null;

  return (
    // Mobile: width-driven 3:4 frame. Desktop: an explicit, viewport-relative
    // height (like the scan viewport's fixed h-[587px]) keeps the frame large
    // while guaranteeing it can never collapse — the prior flex-fill chain
    // (flex-1 → grid-rows-1 → h-full) rendered fine in dev but collapsed to
    // zero height in the production CSS build.
    <button
      type="button"
      onClick={onActivate}
      aria-pressed={active}
      aria-label={`${label} slot`}
      className="block w-full bg-transparent border-0 p-0 cursor-pointer"
    >
      <div className="text-center font-display uppercase text-[10px] lg:text-[11px] tracking-[0.2em] text-bone-dim mb-2 lg:mb-2.5">
        {label}
      </div>
      {/* desktop: center the aspect-locked frame within its column */}
      <div className="lg:flex lg:justify-center">
        <div
          className={[
            'relative aspect-[3/4] w-full rounded-thumb overflow-hidden bg-[#121211]',
            'lg:w-auto lg:max-w-full lg:h-[clamp(280px,42vh,520px)]',
            'border-[1.5px] transition-colors duration-200',
            active ? 'border-aurum' : 'border-[rgba(255,255,255,0.14)]',
          ].join(' ')}
        >
          {filled ? (
            <>
              <img
                src={previewUrl}
                alt={`${label} scan`}
                className="absolute inset-0 w-full h-full object-cover"
              />
              {!freshUpload && entry && (
                <PortraitScoreBar score={formatScore10(entry.overall_score)} size="lg" />
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <LuCirclePlus
                size={34}
                strokeWidth={1.3}
                className={active ? 'text-aurum' : 'text-bone-dim/60'}
                aria-hidden="true"
              />
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

// Caption (capture date / prompt) + a clear upload CTA for one slot. Rendered in
// a row beneath the frames so both sides line up under their frames.
//   Jun 20                ← archive: read-only date · fresh: calendar picker · empty: "Tap to select"
//   [ ⬆ UPLOAD PHOTO ]    ← clear bordered CTA, targets this slot's side
export const GlowupSlotFooter = ({ label, entry, freshUpload, onUpload, onSetDate }) => {
  const filled = Boolean(entry || freshUpload);

  let caption;
  if (!filled) {
    caption = <span className="font-display text-sm text-bone-dim">Tap to select</span>;
  } else if (freshUpload) {
    caption = <GlowupDatePicker label={label} entry={freshUpload} onSave={onSetDate} />;
  } else {
    caption = <span className="font-display text-sm text-bone-dim">{formatScanDate(entry)}</span>;
  }

  return (
    <div>
      <div className="text-center min-h-[24px] flex items-center justify-center">
        {caption}
      </div>
      {onUpload && (
        <button
          type="button"
          onClick={onUpload}
          aria-label={`Upload a photo for the ${label} slot`}
          className="mt-2.5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-field border border-[rgba(255,255,255,0.16)] font-display uppercase text-[11px] tracking-[0.2em] text-bone-dim hover:text-aurum hover:border-[rgba(201,164,92,0.5)] transition-colors duration-200 bg-transparent cursor-pointer"
        >
          <LuUpload size={13} aria-hidden="true" />
          Upload photo
        </button>
      )}
    </div>
  );
};

export default GlowupSlot;
